import type {
    CatalogFilterParams,
    CatalogImportSummary,
    CatalogItem,
    CatalogRequest,
    DataResponse,
    PaginatedCatalogResponse,
} from '@/types';
import type { AxiosResponse } from 'axios';
import api from './api';

class CatalogService {
    /**
     * Fetch all catalog items for the current company
     */
    async getItems(params: CatalogFilterParams = {}): Promise<AxiosResponse<PaginatedCatalogResponse>> {
        return api.get('/api/catalog-items', { params });
    }

    /**
     * Fetch a single catalog item
     */
    async getItem(id: string): Promise<AxiosResponse<DataResponse<CatalogItem>>> {
        return api.get(`/api/catalog-items/${id}`);
    }

    /**
     * Create a new catalog item
     */
    async store(data: CatalogRequest): Promise<AxiosResponse<DataResponse<CatalogItem>>> {
        return api.post('/api/catalog-items', data);
    }

    /**
     * Update an existing catalog item
     */
    async update(id: string, data: Partial<CatalogRequest>): Promise<AxiosResponse<DataResponse<CatalogItem>>> {
        return api.put(`/api/catalog-items/${id}`, data);
    }

    /**
     * Delete (soft-delete) a catalog item
     */
    async delete(id: string): Promise<AxiosResponse<void>> {
        return api.delete(`/api/catalog-items/${id}`);
    }

    /**
     * Import catalog items from a CSV file
     */
    async importCsv(
        file: File,
        companyId: string,
        cleanImport = false,
    ): Promise<AxiosResponse<DataResponse<CatalogImportSummary>>> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('company_id', companyId);
        formData.append('clean_import', cleanImport ? '1' : '0');

        return api.post('/api/catalog-items/import', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
}

export default new CatalogService();
